import { Skeleton } from '@/components/ui/skeleton';
import { useMe } from '@/hooks/use-auth';
import { Navigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';

interface AuthGateProps {
  children: ReactNode;
}

/**
 * Auth guard pras rotas privadas. Faz GET /auth/me; **qualquer falha**
 * (401, 404, network error, parse error) é tratada como "não autenticado"
 * e redireciona pra /login.
 *
 * Decisão consciente de não diferenciar 401 de outros erros: o usuário não
 * tem como agir em "API offline" ou "404 do mock" — em todos os casos a
 * próxima ação é tentar logar de novo. Bloquear com fallback "tente
 * recarregar" só atrapalha em dev (onde MSW pode levar 1-2s pra inicializar
 * e a primeira call falha).
 */
export function AuthGate({ children }: AuthGateProps) {
  const me = useMe();

  if (me.isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/40 p-6">
        <div className="w-full max-w-md space-y-3">
          <Skeleton className="h-8 w-1/2" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </div>
    );
  }

  if (me.isError) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}
